import React, { useContext, useEffect, useMemo, useState } from "react";
import { Context } from "../../../context/Context";
import { fetchMonthlysAPI } from "../../../utils/api/report";
import { FEATURE_NAMES, isFeatureEnabled } from "../../../utils/features";
import { defaultRange } from "../../../utils/analysisWindow";
import useAnalysisWindow from "../../../utils/useAnalysisWindow";
import { getReportText } from "../../reports/reportText";
import { translations } from "../../../utils/lang";
import { Button } from "../../ui";
import CombinedBarLineChart from "../../analysis/charts/barLine/CombinedBarLineChart";
import { BarChartAnalysis } from "../../analysis/charts/bar/BarChartAnalysis";
import FilterUI from "../../analysis/filter/FilterUI";
import {
  buildDashboardAnalyticsData,
  filterDashboardAnalyticsData,
  getDashboardAnalyticsFilterOptions,
  prepareHorizontalBarAnalysis,
} from "./dashboardAnalytics.utils";
import "./DashboardAnalytics.css";

const EMPTY_FILTERS = { app: [], branch: [], month: [], year: [] };

const DashboardAnalytics = ({ storeIds }) => {
  const context = useContext(Context) || {};
  const { lang = "ar", storeAccess = [] } = context;
  const requestedStoreIds = storeIds ?? storeAccess;
  const storeIdsKey = JSON.stringify(Array.isArray(requestedStoreIds) ? requestedStoreIds : []);
  const [requestVersion, setRequestVersion] = useState(0);
  const [status, setStatus] = useState("loading");
  const [rawData, setRawData] = useState([]);
  const [filtersDaily, setFiltersDaily] = useState(EMPTY_FILTERS);
  const copy = translations[lang]?.dashboardAnalytics || translations.en.dashboardAnalytics;

  // REPORT_ANALYSIS_WINDOW: the last 12 months through the bounded report route instead of the whole history.
  const windowOn = isFeatureEnabled(FEATURE_NAMES.REPORT_ANALYSIS_WINDOW);
  const range = useMemo(() => defaultRange(), []);
  const win = useAnalysisWindow({ kinds: ["monthly"], storeIds: JSON.parse(storeIdsKey), range, enabled: windowOn });

  const filterOptions = useMemo(
    () => getDashboardAnalyticsFilterOptions(rawData),
    [rawData],
  );
  const filteredData = useMemo(
    () => filterDashboardAnalyticsData(rawData, filtersDaily),
    [rawData, filtersDaily],
  );
  const analytics = useMemo(
    () => ({
      chartData: buildDashboardAnalyticsData(filteredData, lang, translations),
      percentageData: prepareHorizontalBarAnalysis(filteredData, lang),
    }),
    [filteredData, lang],
  );
  const hasActiveFilters = Object.values(filtersDaily).some((values) => values.length);
  const hasFilteredAnalytics = Boolean(analytics.chartData || analytics.percentageData);

  const handleFilterChangeDaily = (category, values) => {
    setFiltersDaily((current) => ({ ...current, [category]: values }));
  };

  useEffect(() => {
    if (windowOn) return undefined;
    let active = true;

    const fetchAnalytics = async () => {
      setStatus("loading");
      const token = localStorage.getItem("auth-token");
      if (!token) {
        if (active) setStatus("error");
        return;
      }

      try {
        const monthly = await fetchMonthlysAPI(token, JSON.parse(storeIdsKey));
        if (!active) return;
        if (!Array.isArray(monthly)) {
          setStatus("error");
          return;
        }

        setRawData(monthly);
        setStatus(
          monthly.some(
            (record) => record && typeof record.app === "string" && record.app.trim(),
          )
            ? "success"
            : "empty",
        );
      } catch {
        if (active) setStatus("error");
      }
    };

    fetchAnalytics();
    return () => {
      active = false;
    };
  }, [requestVersion, storeIdsKey, windowOn]);

  useEffect(() => {
    if (!windowOn) return;
    if (win.loading) {
      setStatus("loading");
      return;
    }
    if (win.error || !Array.isArray(win.monthly)) {
      setStatus("error");
      return;
    }
    setRawData(win.monthly);
    setStatus(
      win.monthly.some((record) => record && typeof record.app === "string" && record.app.trim())
        ? "success"
        : "empty",
    );
  }, [windowOn, win.loading, win.error, win.monthly]);

  useEffect(() => {
    setFiltersDaily(EMPTY_FILTERS);
  }, [storeIdsKey]);

  return (
    <section className="dashboard-analytics" aria-labelledby="dashboard-analytics-title">
      {status === "loading" && (
        <div className="dashboard-analytics__state" role="status" aria-live="polite">
          <span className="dashboard-analytics__spinner" aria-hidden="true" />
          <span>{copy.loading}</span>
        </div>
      )}

      {status === "empty" && (
        <div className="dashboard-analytics__state" role="status">
          <strong>{copy.empty}</strong>
          <span>{copy.emptyDescription}</span>
        </div>
      )}

      {status === "error" && (
        <div className="dashboard-analytics__state dashboard-analytics__state--error" role="alert">
          <strong>{copy.error}</strong>
          <span>{copy.errorDescription}</span>
          <Button size="sm" appearance="outline" onClick={() => (windowOn ? win.reload() : setRequestVersion((value) => value + 1))}>
            {copy.retry}
          </Button>
        </div>
      )}

      {status === "success" && (
        <div className="dashboard-analytics__charts">
          {windowOn && (
            <p className="dashboard-analytics__window" role="status">
              {getReportText(lang).analysisWindow.shown((win.windows.monthly || range).from, (win.windows.monthly || range).to)}
            </p>
          )}
          <div className="dashboard-analytics__filters" aria-label={copy.filtersLabel}>
            <FilterUI fullWidth={false} label="App" options={filterOptions.apps} selected={filtersDaily.app} onChange={(vals) => handleFilterChangeDaily("app", vals)} />
            <FilterUI fullWidth={false} label="Branch" options={filterOptions.branches} selected={filtersDaily.branch} onChange={(vals) => handleFilterChangeDaily("branch", vals)} />
            <FilterUI fullWidth={false} label="Month" options={filterOptions.months} selected={filtersDaily.month} onChange={(vals) => handleFilterChangeDaily("month", vals)} />
            <FilterUI fullWidth={false} label="Year" options={filterOptions.years} selected={filtersDaily.year} onChange={(vals) => handleFilterChangeDaily("year", vals)} />
            {hasActiveFilters && (
              <Button size="sm" appearance="outline" onClick={() => setFiltersDaily(EMPTY_FILTERS)}>
                {copy.clearFilters}
              </Button>
            )}
          </div>

          {!hasFilteredAnalytics ? (
            <div className="dashboard-analytics__state" role="status">
              <strong>{hasActiveFilters ? copy.noResults : copy.empty}</strong>
              <span>{hasActiveFilters ? copy.noResultsDescription : copy.emptyDescription}</span>
            </div>
          ) : (
            <>
              <section className="dashboard-analytics__percentage">
                <header className="dashboard-analytics__header">
                  <div>
                    <h2 id="dashboard-analytics-title">{copy.title}</h2>
                    <p>{copy.description}</p>
                  </div>
                </header>
                {analytics.chartData && (
                  <CombinedBarLineChart
                    title={copy.title}
                    xData={analytics.chartData.months}
                    xLabel={copy.month}
                    height={400}
                    yAxis={[
                      { id: "amount", position: "left", width: 70, label: copy.amount },
                      { id: "count", position: "right", width: 60, label: copy.count },
                    ]}
                    barSeries={analytics.chartData.barSeries}
                    lineSeries={analytics.chartData.lineSeries}
                  />
                )}
              </section>

              <section className="dashboard-analytics__percentage" aria-labelledby="app-percentage-title">
                <div className="dashboard-analytics__subheader">
                  <h3 id="app-percentage-title">{copy.percentageTitle}</h3>
                  <p>{copy.percentageDescription}</p>
                </div>
                {analytics.percentageData ? (
                  <BarChartAnalysis
                    layout="horizontal"
                    height={Math.max(320, analytics.percentageData.labels.length * 72)}
                    xAxis={[{ valueFormatter: (value) => `${value}%` }]}
                    yAxis={[{
                      data: analytics.percentageData.labels,
                      scaleType: "band",
                      colorMap: {
                        type: "ordinal",
                        values: analytics.percentageData.labels,
                        colors: analytics.percentageData.colors,
                      },
                    }]}
                    series={[
                      { id: "service-charge", label: copy.serviceCharge, data: analytics.percentageData.serviceCharge, valueFormatter: (value) => `${value}%` },
                      { id: "efficiency", label: copy.efficiency, data: analytics.percentageData.efficiency, valueFormatter: (value) => `${value}%` },
                    ]}
                  />
                ) : (
                  <div className="dashboard-analytics__state dashboard-analytics__state--compact" role="status">
                    <strong>{copy.empty}</strong>
                    <span>{copy.emptyDescription}</span>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default DashboardAnalytics;
export { DashboardAnalytics };
